import React, { useCallback, useEffect, useMemo, useState } from "react";
import { InputText, Modal } from "../../components";
import { updateDocAPI } from "../../common/services";
import { useToaster } from "../../common/contexts/toast";
import { errorMessageOf } from "../../utils";

const EditDocDetailsModal = ({ visible, doc, onClose, onUpdated }) => {
    const { showToaster } = useToaster();
    const [displayName, setDisplayName] = useState("");
    const [documentNo, setDocumentNo] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setDisplayName(doc?.displayName || "");
        setDocumentNo(doc?.documentNo || "");
    }, [visible, doc]);

    const patch = useMemo(() => {
        const next = {};
        const name = displayName.trim();
        const number = documentNo.trim();
        if (name !== (doc?.displayName || "")) next.displayName = name;
        if (number !== (doc?.documentNo || "")) next.documentNo = number;
        return next;
    }, [displayName, documentNo, doc]);

    const canApply = Boolean(Object.keys(patch).length) && Boolean(displayName.trim()) && !saving;

    const handleApply = useCallback(async () => {
        if (!canApply || !doc?.id) return;
        setSaving(true);
        try {
            const res = await updateDocAPI(doc.id, patch);
            showToaster && showToaster("doc-edit-success", "The file details have been updated.", true);
            onUpdated && onUpdated(res?.data?.doc || { ...doc, ...patch });
            onClose && onClose();
        } catch (error) {
            showToaster && showToaster("doc-edit-error", errorMessageOf(error, "Couldn't update the file details."), false);
        } finally {
            setSaving(false);
        }
    }, [canApply, doc, patch, onUpdated, onClose, showToaster]);

    return (
        <Modal
            visible={visible}
            title="Edit details"
            size="large"
            modalClass="edit-doc-details-modal"
            closable={!saving}
            onClose={() => !saving && onClose && onClose()}
            onClickCancel={() => !saving && onClose && onClose()}
            onClickConfirm={handleApply}
            cancelButtonName="CANCEL"
            cancelButtonDisable={saving}
            confirmButtonName="APPLY"
            confirmButtonDisable={!canApply}
            confirmButtonLoading={saving}
        >
            <div className="edit-doc-details-form">
                <InputText
                    label="Document name"
                    placeholder="Type"
                    value={displayName}
                    readOnly={saving}
                    onChange={(value) => setDisplayName(value)}
                />
                <InputText
                    label="Document ID"
                    placeholder="Type"
                    value={documentNo}
                    readOnly={saving}
                    onChange={(value) => setDocumentNo(value)}
                />
            </div>
        </Modal>
    );
};

export default EditDocDetailsModal;
